import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  IconButton,
  Tooltip,
  Chip
} from '@mui/material';
import {
  Send as SendIcon,
  CallReceived as ReceivedIcon,
  Sms as SmsIcon,
  AttachMoney as CostIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import axios from 'axios';

interface UsageRecord {
  id: string;
  clientId: string;
  periodStart: string;
  periodEnd: string;
  messagesSent: number;
  messagesReceived: number;
  segments: number;
  cost: number;
  collectedAt: string;
}

function getMonthOptions(): { label: string; startDate: string; endDate: string }[] {
  const options = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const start = new Date(d.getFullYear(), d.getMonth(), 1);
    const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    const label = start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    options.push({
      label,
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0],
    });
  }
  return options;
}

function formatCost(cost: number): string { return '$' + cost.toFixed(4); }
function formatDate(dateStr: string): string { return dateStr ? new Date(dateStr).toLocaleDateString() : ''; }

const thStyle = { fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '0.6875rem' as const, textTransform: 'uppercase' as const, color: '#475569', letterSpacing: 0.5 };

const ClientUsageDashboard: React.FC = () => {
  const monthOptions = getMonthOptions();
  const [selectedMonth, setSelectedMonth] = useState(0);
  const [usageRecords, setUsageRecords] = useState<UsageRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [limitStatus, setLimitStatus] = useState<{ limit: number | null; used: number; remaining: number | null } | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const summary = usageRecords.reduce(
    (acc, r) => ({ messagesSent: acc.messagesSent + r.messagesSent, messagesReceived: acc.messagesReceived + r.messagesReceived, segments: acc.segments + r.segments, cost: acc.cost + r.cost }),
    { messagesSent: 0, messagesReceived: 0, segments: 0, cost: 0 }
  );

  const fetchUsage = useCallback(async (showLoader = true) => {
    try {
      if (showLoader) setLoading(true);
      setError('');
      const { startDate, endDate } = monthOptions[selectedMonth];
      const response = await axios.get('/api/usage', { params: { startDate, endDate } });
      setUsageRecords(response.data.usage || []);
    } catch (err: any) { setError(err.response?.data?.error || 'Failed to load usage data'); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth]);

  useEffect(() => { fetchUsage(true); }, [fetchUsage]);

  // Auto-collect on first load only
  useEffect(() => {
    const collectOnLoad = async () => {
      try { await axios.post('/api/usage/collect'); } catch (e) { /* ignore */ }
      fetchUsage(true);
      fetchLimitStatus();
    };
    collectOnLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLimitStatus = async () => {
    try {
      const res = await axios.get('/api/usage/limit');
      setLimitStatus(res.data);
    } catch (err) { /* limit endpoint may not exist on older deploys */ }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      // Trigger usage collection from Twilio first
      await axios.post('/api/usage/collect');
    } catch (err) {
      // Collection might fail if no subaccount configured, still fetch existing data
      console.error('Usage collection error:', err);
    }
    await fetchUsage();
    await fetchLimitStatus();
    setRefreshing(false);
  };

  const handleMonthChange = (e: SelectChangeEvent<number>) => {
    setSelectedMonth(Number(e.target.value));
  };

  const cards = [
    { label: 'Messages Sent', value: summary.messagesSent, icon: <SendIcon />, color: '#4b2bee' },
    { label: 'Messages Received', value: summary.messagesReceived, icon: <ReceivedIcon />, color: '#00b894' },
    { label: 'Segments', value: summary.segments, icon: <SmsIcon />, color: '#0984e3' },
    { label: 'Estimated Cost', value: formatCost(summary.cost), icon: <CostIcon />, color: '#e17055' },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
        <Box>
          <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: { xs: "1.25rem", md: "1.75rem" }, color: '#0f172a', mb: 0.5 }}>
            My Usage
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Refresh usage data">
              <IconButton onClick={handleRefresh} disabled={refreshing} size="small" sx={{ color: '#4b2bee' }}>
                {refreshing ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <FormControl size="small" sx={{ minWidth: { xs: 140, md: 200 } }}>
          <InputLabel>Billing Period</InputLabel>
          <Select value={selectedMonth} label="Billing Period" onChange={handleMonthChange} sx={{ borderRadius: 2 }}>
            {monthOptions.map((opt, i) => (
              <MenuItem key={i} value={i}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Monthly Limit */}
      {limitStatus && limitStatus.limit !== null && (
        <Box sx={{ mb: 3, p: 2, bgcolor: '#ffffff', borderRadius: 2, boxShadow: '0px 4px 12px rgba(15, 23, 42, 0.04)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '0.8125rem', color: '#475569' }}>
              {monthOptions[selectedMonth].label} Limit:
            </Typography>
            <Chip
              label={`$${summary.cost.toFixed(2)} / $${limitStatus.limit.toFixed(2)} used`}
              size="small"
              sx={{
                bgcolor: summary.cost >= limitStatus.limit ? 'rgba(225,112,85,0.1)' : 'rgba(75,43,238,0.1)',
                color: summary.cost >= limitStatus.limit ? '#e17055' : '#4b2bee',
                fontWeight: 600,
                fontSize: '0.75rem'
              }}
            />
          </Box>
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', color: '#94a3b8' }}>
            ${Math.max(0, limitStatus.limit - summary.cost).toFixed(2)} remaining
          </Typography>
        </Box>
      )}

      {/* Period label */}
      <Typography sx={{ fontFamily: 'Inter, sans-serif', color: '#475569', fontSize: '0.875rem', mb: 2, fontWeight: 500 }}>
        Showing usage for: <strong>{monthOptions[selectedMonth].label}</strong>
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress sx={{ color: '#4b2bee' }} /></Box>
      ) : error ? (
        <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
      ) : (
        <>
          {/* Summary Cards */}
          <Grid container spacing={2.5} sx={{ mb: 4 }}>
            {cards.map((card) => (
              <Grid item xs={12} sm={6} md={3} key={card.label}>
                <Box sx={{ bgcolor: '#ffffff', borderRadius: 3, p: 3, boxShadow: '0px 12px 32px rgba(15, 23, 42, 0.04)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: card.color + '10', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {React.cloneElement(card.icon, { sx: { color: card.color, fontSize: 20 } })}
                    </Box>
                    <Box>
                      <Typography sx={{ fontFamily: 'Inter, sans-serif', color: '#475569', fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 500 }}>
                        {card.label}
                      </Typography>
                      <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: '1.5rem', color: '#0f172a' }}>
                        {card.value}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Usage History */}
          <Box sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0px 12px 32px rgba(15, 23, 42, 0.04)', overflow: 'hidden' }}>
            <Box sx={{ p: 3, pb: 2 }}>
              <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: '1.125rem', color: '#0f172a' }}>
                Daily Breakdown
              </Typography>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={thStyle}>Date</TableCell>
                    <TableCell align="right" sx={thStyle}>Sent</TableCell>
                    <TableCell align="right" sx={thStyle}>Received</TableCell>
                    <TableCell align="right" sx={thStyle}>Segments</TableCell>
                    <TableCell align="right" sx={thStyle}>Cost</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {usageRecords.length === 0 ? (
                    <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: '#475569' }}>No usage records for this period.</TableCell></TableRow>
                  ) : (
                    usageRecords.map((record) => (
                      <TableRow key={record.id} sx={{ '&:hover': { bgcolor: 'rgba(75,43,238,0.04)' } }}>
                        <TableCell sx={{ fontFamily: 'Inter, sans-serif', color: '#0f172a', fontSize: '0.875rem' }}>{formatDate(record.periodStart)}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 600, color: '#0f172a' }}>{record.messagesSent}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 600, color: '#0f172a' }}>{record.messagesReceived}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 600, color: '#0f172a' }}>{record.segments}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 600, color: '#0f172a' }}>{formatCost(record.cost)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </>
      )}
    </Box>
  );
};

export default ClientUsageDashboard;
